import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Check, Factory, FileText, Home, ImageIcon, Mail, MapPin, MessageCircle, Paperclip, Phone, Route, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { COMPANY, IMG, PEOPLE, PRICES } from "@/lib/content";
import { Btn, Eyebrow, Field, Photo, SectionHead } from "@/components/public/ui";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EASE, Reveal } from "@/components/public/Reveal";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   Content
   ------------------------------------------------------------------ */
const MARKET_OPTIONS = [
  { id: "service_bustad", label: "Heim eller hytte", desc: "Service, feilretting, ladar, sikringsskap", icon: Home, placeholder: "T.d. «Vi treng ny heimeladar i garasjen. Sikringsskapet står i kjellaren, om lag 8 meter unna.»" },
  { id: "bygg_naering", label: "Næringsbygg", desc: "Butikk, kontor, verkstad, sameige", icon: Building2, placeholder: "T.d. «Vi skal byggje om kontorlokala og treng nytt lys og fleire kursar. Ønskjer synfaring.»" },
  { id: "industri", label: "Industri", desc: "Prosess, automasjon, vedlikehald", icon: Factory, placeholder: "T.d. «Vi treng tilbod på ombygging av ei hovudfordeling og ny motorstyring til to pumper.»" },
  { id: "samferdsel", label: "Veg og tunnel", desc: "Tunnel, SRO, vegljos", icon: Route, placeholder: "T.d. «Oppgradering av elektro og SRO i tunnel. Ønskjer kontakt om kapasitet og tilbod.»" },
];
const MARKET_LABEL = Object.fromEntries(MARKET_OPTIONS.map((m) => [m.id, m.label]));

const TIMING = ["Så snart som råd", "I løpet av nokre veker", "Eg planlegg fram i tid"];

const FAQ = [
  ["Kostar det noko å sende ei førespurnad?", "Nei. Innsendinga er uforpliktande. Vi avklarer omfang, prisgrunnlag og tidspunkt med deg før arbeidet startar."],
  ["Må eg vite nøyaktig kva eg treng?", "Nei. Skriv det du veit, og legg gjerne ved bilete av anlegget eller området. Vi stiller spørsmåla som trengst når vi tek kontakt."],
  ["Kva skjer etter at eg har sendt?", "Du får eit referansenummer med ein gong. Førespurnaden går til rett fagperson hos oss, som tek kontakt på telefon eller e-post for å avklare detaljar, synfaring eller tidspunkt."],
  ["Eg har ein feil som hastar. Kva gjer eg?", `Ring oss på ${COMPANY.phone}. Feilretting utanom ordinær arbeidstid gjer vi etter avtale.`],
  ["Tek de oppdrag utanfor Kvinnherad?", "Serviceoppdrag tek vi i hovudsak i Kvinnherad. Større prosjekt innan industri, samferdsel og bygg tek vi fleire stader på Vestlandet."],
];

const OFFICES = [
  { name: "Rosendal", role: "Hovudkontor", lines: ["Vangsvegen 13", "5486 Rosendal"] },
  { name: "Husnes", role: "Avdeling", lines: ["Næring, bustad og service"] },
  { name: "Halsnøy", role: "Avdeling", lines: ["Næring, bustad og service"] },
];

const EMPTY = { name: "", company: "", email: "", phone: "", address: "", market: "service_bustad", message: "", timing: "" };
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} kB`);
const fileKey = (f) => `${f.name}-${f.size}-${f.lastModified}`;
const MONTHS = ["januar", "februar", "mars", "april", "mai", "juni", "juli", "august", "september", "oktober", "november", "desember"];
const fmtWhen = (d) => `${d.getDate()}. ${MONTHS[d.getMonth()]} kl. ${String(d.getHours()).padStart(2, "0")}.${String(d.getMinutes()).padStart(2, "0")}`;

/* ------------------------------------------------------------------
   Small pieces
   ------------------------------------------------------------------ */
const StepMark = ({ n, done }) => (
  <span className="flex items-center gap-3">
    <span className={cn("t-mono inline-flex h-7 min-w-[28px] items-center justify-center rounded-full border px-2 transition-colors duration-200", done ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground")} aria-hidden>
      {done ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : n}
    </span>
  </span>
);

const FormSection = ({ n, title, text, done, children, testid }) => (
  <section className="grid gap-6 border-t border-border py-10 first:border-t-0 first:pt-0 lg:grid-cols-12 lg:gap-10" data-testid={testid}>
    <div className="lg:col-span-4">
      <div className="flex items-start gap-4">
        <StepMark n={n} done={done} />
        <div>
          <h2 className="t-h3">{title}</h2>
          {text && <p className="mt-2 max-w-[30ch] text-[14.5px] leading-relaxed text-muted-foreground">{text}</p>}
        </div>
      </div>
    </div>
    <div className="min-w-0 lg:col-span-8">{children}</div>
  </section>
);

/* Segmented choice with a sliding selection */
const Segments = ({ options, value, onChange, name, testid, allowEmpty = false }) => (
  <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => {
      const on = value === o;
      return (
        <button
          key={o}
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => onChange(on && allowEmpty ? "" : o)}
          data-testid={`${testid}-option-${o.toLowerCase().replace(/[^a-zæøå0-9]+/g, "-")}`}
          className={cn(
            "inline-flex h-11 items-center justify-center rounded-[10px] border px-4 text-[14.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            on ? "border-foreground bg-foreground text-background" : "border-input bg-background text-foreground/80 hover:border-foreground/60 hover:text-foreground"
          )}
        >
          {o}
        </button>
      );
    })}
  </div>
);

/* Live summary rows: value fades in when it goes from empty to filled. */
const TicketRow = ({ label, value, muted = false, children }) => (
  <div className="grid grid-cols-[88px_1fr] items-baseline gap-4 py-3.5">
    <dt className="text-[13px] text-muted-foreground">{label}</dt>
    <dd className="min-w-0">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={value ? "v" : "e"} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.22, ease: EASE }} className={cn("text-[14.5px] leading-snug", !value && "text-muted-foreground/60", muted && "text-muted-foreground")}>
          {children || value || "—"}
        </motion.div>
      </AnimatePresence>
    </dd>
  </div>
);

/* ------------------------------------------------------------------
   Page
   ------------------------------------------------------------------ */
export default function Contact() {
  const [params] = useSearchParams();
  const reduce = useReducedMotion();
  const [form, setForm] = useState(EMPTY);
  const [service, setService] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState({});
  const previewsRef = useRef({});
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);
  const formRef = useRef(null);

  /* Prefill from /prisar (?teneste=) or /karriere (?emne=) */
  useEffect(() => {
    const t = params.get("teneste");
    const e = params.get("emne");
    const g = params.get("gjeld");
    if (g && MARKET_LABEL[g]) setForm((f) => ({ ...f, market: g }));
    if (t) {
      const item = PRICES.find((p) => p.id === t);
      if (item) {
        setService(item.id);
        setForm((f) => ({ ...f, market: item.market }));
      }
    } else if (e) {
      setForm((f) => ({ ...f, message: f.message || `${e}\n\n` }));
    }
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const market = MARKET_OPTIONS.find((m) => m.id === form.market) || MARKET_OPTIONS[0];
  const quickPicks = useMemo(() => PRICES.filter((p) => p.market === form.market), [form.market]);
  const picked = PRICES.find((p) => p.id === service);

  const invalid = {
    name: !form.name.trim(),
    email: !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()),
    message: !picked && form.message.trim().length < 5,
  };
  const hasErrors = invalid.name || invalid.email || invalid.message;
  const err = (k) => touched && invalid[k];

  /* Progress for the summary ticket */
  const stepDone = [true, !invalid.message, !invalid.name && !invalid.email, Boolean(done)];
  const doneCount = stepDone.filter(Boolean).length;

  /* Attachments + image previews */
  const addFiles = useCallback((list) => {
    const arr = Array.from(list || []);
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of arr) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name} er for stor (maks 10 MB)`);
          continue;
        }
        if (!merged.find((m) => fileKey(m) === fileKey(f))) merged.push(f);
      }
      if (merged.length > 8) toast.error("Du kan leggje ved inntil 8 filer.");
      return merged.slice(0, 8);
    });
  }, []);

  useEffect(() => {
    setPreviews((prev) => {
      const next = {};
      for (const f of files) {
        const k = fileKey(f);
        if (f.type.startsWith("image/")) next[k] = prev[k] || URL.createObjectURL(f);
      }
      Object.entries(prev).forEach(([k, url]) => { if (!next[k]) URL.revokeObjectURL(url); });
      return next;
    });
  }, [files]);
  useEffect(() => { previewsRef.current = previews; }, [previews]);
  useEffect(() => () => Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u)), []);

  const removeFile = (f) => setFiles((p) => p.filter((x) => fileKey(x) !== fileKey(f)));

  const composedMessage = () => {
    const parts = [];
    if (picked) parts.push(`Teneste: ${picked.name} (${picked.price})`);
    if (form.message.trim()) parts.push(form.message.trim());
    return parts.join("\n\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (hasErrors) {
      toast.error("Fyll inn namn, e-post og nokre ord om kva du treng hjelp med.");
      const first = formRef.current?.querySelector('[aria-invalid="true"]');
      first?.focus();
      first?.scrollIntoView({ block: "center", behavior: reduce ? "instant" : "smooth" });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, message: composedMessage() }).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/public/inquiries", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone({ ...r.data, at: new Date(), summary: { market: market.label, service: picked?.name, address: form.address, timing: form.timing, files: files.length, name: form.name, email: form.email, phone: form.phone } });
      toast.success(`Takk. Referanse ${r.data.ref}`);
      window.scrollTo({ top: 0, behavior: reduce ? "instant" : "smooth" });
    } catch (error) {
      toast.error(errMsg(error, "Vi fekk ikkje sendt førespurnaden. Sjekk nettet og prøv på nytt."));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setDone(null); setForm(EMPTY); setService(""); setFiles([]); setTouched(false); };
  const openChat = () => window.dispatchEvent(new CustomEvent("ke:open-chat"));

  const summary = done?.summary || { market: market.label, service: picked?.name, address: form.address, timing: form.timing, files: files.length, name: form.name, email: form.email, phone: form.phone };

  /* ---------- Ticket (sticky on desktop) ---------- */
  const ticket = (
    <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-card" data-testid="public-contact-ticket">
      <div className={cn("grain relative px-6 py-5 text-white transition-colors duration-500", done ? "bg-brand" : "bg-ink")}>
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <p className="t-mono text-white/60">{done ? "Førespurnad motteken" : "Førespurnaden din"}</p>
            <p className="t-num mt-1 text-[1.75rem] leading-none" data-testid="public-contact-ticket-ref">{done ? done.ref : "KE-····"}</p>
          </div>
          <span className="t-mono rounded-full border border-white/20 px-2.5 py-1 text-white/80" data-testid="public-contact-ticket-progress">{done ? "Sendt" : `${doneCount} av 4`}</span>
        </div>
        <div className="relative mt-5 grid grid-cols-4 gap-1.5" aria-hidden>
          {stepDone.map((d, i) => (
            <span key={i} className={cn("h-1 rounded-full transition-colors duration-300", d ? "bg-white" : "bg-white/20")} />
          ))}
        </div>
      </div>
      <dl className="divide-y divide-border px-6">
        <TicketRow label="Gjeld" value={summary.market}>
          <span className="font-medium">{summary.market}</span>
          {summary.service && <span className="block text-[13.5px] text-muted-foreground">{summary.service}</span>}
        </TicketRow>
        <TicketRow label="Stad" value={summary.address} />
        <TicketRow label="Tidspunkt" value={summary.timing} />
        <TicketRow label="Vedlegg" value={summary.files ? String(summary.files) : ""}>
          {summary.files ? (
            <span className="flex items-center gap-2">
              {!done && (
                <span className="flex -space-x-1.5">
                  {files.slice(0, 4).map((f) => {
                    const url = previews[fileKey(f)];
                    return url ? <img key={fileKey(f)} src={url} alt="" className="h-6 w-6 rounded-md border border-background object-cover" /> : <span key={fileKey(f)} className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-background bg-secondary"><FileText className="h-3 w-3" /></span>;
                  })}
                </span>
              )}
              <span>{summary.files} {summary.files === 1 ? "fil" : "filer"}</span>
            </span>
          ) : null}
        </TicketRow>
        <TicketRow label="Kontakt" value={summary.name || summary.phone || summary.email}>
          {summary.name && <span className="block font-medium">{summary.name}</span>}
          {(summary.phone || summary.email) && <span className="block text-[13.5px] text-muted-foreground">{[summary.phone, summary.email].filter(Boolean).join(" · ")}</span>}
        </TicketRow>
      </dl>
      <p className="border-t border-border bg-secondary/60 px-6 py-4 text-[13px] leading-relaxed text-muted-foreground">
        {done ? "Vi tek kontakt på telefon eller e-post for å avklare jobben." : "Innsendinga er ei førespurnad. Pris og tidspunkt blir avtalt før arbeidet startar."}
      </p>
    </div>
  );

  return (
    <div data-testid="public-contact-page" className="overflow-x-clip">
      {/* ---------- Head ---------- */}
      <section className="container-public pt-10 sm:pt-14 lg:pt-20" data-testid="public-contact-head">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          <motion.div className="lg:col-span-8" initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
            <p className="t-mono flex items-center gap-3"><span>Kontakt</span><span aria-hidden className="h-px w-8 bg-border" /><span>Uforpliktande førespurnad</span></p>
            <h1 className="t-h1 mt-5 max-w-[16ch]">
              Fortel oss kva du treng hjelp med.
              <span className="block text-muted-foreground/70">Vi tek det derifrå.</span>
            </h1>
          </motion.div>
          <motion.div className="flex flex-col justify-end gap-6 lg:col-span-4" initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}>
            <p className="t-lead max-w-md">Nokre setningar er nok. Legg gjerne ved bilete, så tek vi kontakt for å avklare pris og tidspunkt før arbeidet startar.</p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[15px]">
              <a href={COMPANY.phoneHref} className="u-link inline-flex items-center gap-2 font-medium" data-testid="public-contact-phone"><Phone className="h-4 w-4 text-brand" />{COMPANY.phone}</a>
              <a href={`mailto:${COMPANY.email}`} className="u-link inline-flex items-center gap-2 font-medium" data-testid="public-contact-email"><Mail className="h-4 w-4 text-brand" />{COMPANY.email}</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- Form + ticket ---------- */}
      <section className="container-public mt-12 grid min-w-0 gap-10 sm:mt-16 lg:grid-cols-12 lg:gap-14" data-testid="public-contact-booking">
        <div className="min-w-0 lg:col-span-8">
          <AnimatePresence mode="wait" initial={false}>
            {done ? (
              <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} className="rounded-2xl border border-border p-6 sm:p-10" data-testid="public-bestill-success" role="status" aria-live="polite">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand text-brand-foreground"><Check className="h-5 w-5" strokeWidth={2.5} /></span>
                  <p className="t-mono">Førespurnad motteken</p>
                </div>
                <h2 className="t-h2 mt-7">Takk for førespurnaden</h2>
                <p className="mt-4 max-w-lg text-[17px] leading-relaxed text-muted-foreground">Vi har fått meldinga di og tek kontakt for å avklare jobben.</p>

                <div className="mt-10 border-t border-border pt-8">
                  <p className="caption">Referansen din</p>
                  <p className="t-num mt-2 text-[2.6rem] leading-none sm:text-6xl" data-testid="public-bestill-success-ref">{done.ref}</p>
                  <p className="caption mt-3">Vis til referansen om du tek kontakt med oss om saka.</p>
                  <dl className="mt-8 grid gap-3 text-[15px] sm:grid-cols-2 sm:gap-x-10">
                    <div className="grid grid-cols-[88px_1fr] gap-3"><dt className="text-muted-foreground">Sendt</dt><dd className="tabular">{fmtWhen(done.at)}</dd></div>
                    <div className="grid grid-cols-[88px_1fr] gap-3"><dt className="text-muted-foreground">Gjeld</dt><dd>{done.summary.market}{done.summary.service ? ` · ${done.summary.service}` : ""}</dd></div>
                    <div className="grid grid-cols-[88px_1fr] gap-3"><dt className="text-muted-foreground">Vedlegg</dt><dd>{done.attachments || 0} {done.attachments === 1 ? "fil" : "filer"}</dd></div>
                    <div className="grid grid-cols-[88px_1fr] gap-3"><dt className="text-muted-foreground">Svar til</dt><dd className="min-w-0 break-words">{[done.summary.phone, done.summary.email].filter(Boolean).join(" · ")}</dd></div>
                  </dl>
                </div>

                <ol className="mt-10 divide-y divide-border border-y border-border">
                  {[
                    ["No", "Førespurnaden er registrert og sendt til rett fagperson hos oss."],
                    ["Deretter", "Vi tek kontakt på telefon eller e-post for å avklare detaljar, synfaring eller tidspunkt."],
                    ["Etter oppdraget", "Du får dokumentasjon på anlegget når arbeidet er ferdig."],
                  ].map(([t, d], i) => (
                    <li key={t} className="grid gap-1 py-4 sm:grid-cols-[160px_1fr] sm:gap-6">
                      <span className="flex items-center gap-3 font-medium"><span className="t-mono text-muted-foreground">0{i + 1}</span>{t}</span>
                      <span className="text-[15px] leading-relaxed text-muted-foreground">{d}</span>
                    </li>
                  ))}
                </ol>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Btn to="/" variant="dark" data-testid="public-bestill-home-button">Til framsida</Btn>
                  <Btn variant="outline" onClick={reset} data-testid="public-bestill-new-button">Send ei ny førespurnad</Btn>
                </div>
              </motion.div>
            ) : (
              <motion.form key="form" ref={formRef} onSubmit={submit} noValidate initial={false} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="min-w-0" data-testid="public-bestill-form">
                {/* 01 */}
                <FormSection n="01" title="Kva gjeld det?" text="Vel det som passar best. Det hjelper oss å sende førespurnaden til rett person." done={stepDone[0]} testid="public-bestill-step-market">
                  <div role="radiogroup" aria-label="Kva gjeld det" className="grid gap-2.5 sm:grid-cols-2 sm:gap-3" data-testid="public-bestill-market">
                    {MARKET_OPTIONS.map(({ id, label, desc, icon: Icon }) => {
                      const on = form.market === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => { set("market")(id); if (picked && picked.market !== id) setService(""); }}
                          data-testid={`public-bestill-market-option-${id}`}
                          className={cn(
                            "group relative flex items-center gap-4 rounded-2xl border p-4 text-left transition-[border-color,background-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:p-5",
                            on ? "border-foreground bg-foreground text-background shadow-card" : "border-border bg-background hover:border-foreground/50"
                          )}
                        >
                          <span className={cn("inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors", on ? "bg-background/10 text-brand" : "bg-secondary text-foreground/80")}>
                            <Icon className="h-5 w-5" strokeWidth={1.75} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[15.5px] font-medium leading-tight">{label}</span>
                            <span className={cn("mt-1 block text-[13px] leading-snug", on ? "text-background/65" : "text-muted-foreground")}>{desc}</span>
                          </span>
                          <span className={cn("inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors", on ? "border-background/40 bg-background text-foreground" : "border-border")} aria-hidden>
                            {on && <Check className="h-3 w-3" strokeWidth={3} />}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Quick picks from the price list */}
                  <div className="mt-7" data-testid="public-bestill-quickpicks">
                    <div className="flex items-baseline justify-between gap-4">
                      <p className="text-sm font-medium">Vanlege jobbar</p>
                      <p className="text-[13px] text-muted-foreground">valfritt · priseksempel</p>
                    </div>
                    {quickPicks.length ? (
                      <ul role="radiogroup" aria-label="Vanlege jobbar" className="mt-3 border-t border-border">
                        {quickPicks.map((p) => {
                          const on = service === p.id;
                          return (
                            <li key={p.id} className="border-b border-border">
                              <button
                                type="button"
                                role="radio"
                                aria-checked={on}
                                onClick={() => setService(on ? "" : p.id)}
                                data-testid={`public-bestill-service-${p.id}`}
                                className="group flex w-full items-center gap-3 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md min-h-[44px]"
                              >
                                <span className={cn("inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors", on ? "border-brand bg-brand text-brand-foreground" : "border-foreground/30 group-hover:border-foreground")} aria-hidden>
                                  {on && <Check className="h-3 w-3" strokeWidth={3} />}
                                </span>
                                <span className={cn("min-w-0 flex-1 text-[15px] leading-snug", on ? "font-medium" : "text-foreground/85 group-hover:text-foreground")}>{p.name}</span>
                                <span className="t-num shrink-0 text-[14px] text-muted-foreground">{p.price}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="mt-3 text-[14.5px] leading-relaxed text-muted-foreground">Industri- og samferdselsoppdrag prisar vi etter synfaring og tilbod. Skriv kort kva det gjeld, så tek ein prosjektleiar kontakt.</p>
                    )}
                    <AnimatePresence initial={false}>
                      {picked && (
                        <motion.p key={picked.id} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25, ease: EASE }} className="overflow-hidden text-[13.5px] leading-relaxed text-muted-foreground" data-testid="public-bestill-service-note">
                          <span className="block pt-3">{picked.short || picked.desc} Prisen er eit døme; vi stadfestar pris før arbeidet startar.</span>
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </FormSection>

                {/* 02 */}
                <FormSection n="02" title="Om jobben" text="Skriv det du veit. Bilete av anlegget gjer det enklare for oss å vurdere jobben." done={stepDone[1]} testid="public-bestill-step-job">
                  <div className="grid gap-6">
                    <Field label="Kva treng du hjelp med?" hint={picked ? "valfritt når du har valt ein jobb" : undefined} htmlFor="message" error={err("message") ? "Skriv nokre ord om kva du treng hjelp med." : undefined}>
                      <textarea
                        id="message"
                        value={form.message}
                        onChange={set("message")}
                        rows={5}
                        placeholder={market.placeholder}
                        aria-invalid={err("message")}
                        className="field h-auto min-h-[150px] resize-y py-3 leading-relaxed"
                        data-testid="public-bestill-message-input"
                        required={!picked}
                      />
                    </Field>

                    <Field label="Adresse for oppdraget" hint="valfritt" htmlFor="address">
                      <div className="relative">
                        <MapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <input id="address" value={form.address} onChange={set("address")} autoComplete="street-address" placeholder="Gate, stad" className="field !pl-10" data-testid="public-bestill-address-input" />
                      </div>
                    </Field>
                    <Field label="Når passar det?" hint="valfritt">
                      <Segments options={TIMING} value={form.timing} onChange={set("timing")} name="Når passar det" testid="public-bestill-timing" allowEmpty />
                    </Field>

                    <Field label="Legg ved bilete" hint="bilete, teikningar, PDF · inntil 8 filer">
                      <div
                        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                        onDragLeave={() => setDrag(false)}
                        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
                        onClick={() => inputRef.current?.click()}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
                        data-testid="public-bestill-upload-dropzone"
                        className={cn("flex cursor-pointer items-center gap-4 rounded-2xl border border-dashed px-4 py-5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", drag ? "border-foreground bg-secondary" : "border-input bg-background hover:border-foreground/50")}
                      >
                        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary"><Paperclip className="h-[18px] w-[18px]" /></span>
                        <div className="min-w-0">
                          <p className="text-[15px]">Dra filer hit, eller <span className="font-medium underline underline-offset-4">vel filer</span></p>
                          <p className="text-[13px] text-muted-foreground">Eit bilete av sikringsskapet, rommet eller området seier ofte meir enn mange ord. Maks 10 MB per fil.</p>
                        </div>
                        <input ref={inputRef} type="file" multiple accept="image/*,.pdf,.dwg,.dxf,.doc,.docx,.xls,.xlsx" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="public-bestill-file-input" />
                      </div>
                      {files.length > 0 && (
                        <ul className="mt-3 grid grid-cols-3 gap-2.5 sm:grid-cols-4" data-testid="public-bestill-file-list">
                          {files.map((f) => {
                            const url = previews[fileKey(f)];
                            return (
                              <li key={fileKey(f)} className="group relative overflow-hidden rounded-xl border border-border bg-secondary" data-testid="public-bestill-file-item">
                                <div className="aspect-square">
                                  {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : (
                                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">{f.type.startsWith("image/") ? <ImageIcon className="h-5 w-5" /> : <FileText className="h-5 w-5" />}<span className="t-mono uppercase">{(f.name.split(".").pop() || "fil").slice(0, 4)}</span></div>
                                  )}
                                </div>
                                <div className="flex items-center justify-between gap-2 border-t border-border bg-background px-2.5 py-1.5">
                                  <span className="min-w-0 truncate text-[12px]" title={f.name}>{f.name}</span>
                                  <span className="tabular shrink-0 text-[11px] text-muted-foreground">{fmtSize(f.size)}</span>
                                </div>
                                <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(f); }} className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/90 text-foreground shadow-card backdrop-blur transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`Fjern ${f.name}`} data-testid="public-bestill-file-remove-button"><X className="h-3.5 w-3.5" /></button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </Field>
                  </div>
                </FormSection>

                {/* 03 */}
                <FormSection n="03" title="Korleis når vi deg?" text="Vi tek kontakt på telefon eller e-post for å avklare jobben." done={stepDone[2]} testid="public-bestill-step-contact">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Namn" htmlFor="name" error={err("name") ? "Skriv inn namnet ditt." : undefined}>
                      <input id="name" value={form.name} onChange={set("name")} autoComplete="name" aria-invalid={err("name")} className="field" data-testid="public-bestill-name-input" required />
                    </Field>
                    <Field label="Telefon" hint="valfritt" htmlFor="phone">
                      <input id="phone" type="tel" inputMode="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" className="field" data-testid="public-bestill-phone-input" />
                    </Field>
                    <Field label="E-post" htmlFor="email" error={err("email") ? "Skriv inn ei gyldig e-postadresse." : undefined}>
                      <input id="email" type="email" inputMode="email" value={form.email} onChange={set("email")} autoComplete="email" aria-invalid={err("email")} className="field" data-testid="public-bestill-email-input" required />
                    </Field>
                    <Field label="Firma" hint="valfritt" htmlFor="company">
                      <input id="company" value={form.company} onChange={set("company")} autoComplete="organization" className="field" data-testid="public-bestill-company-input" />
                    </Field>
                  </div>
                </FormSection>

                {/* 04 */}
                <FormSection n="04" title="Send førespurnaden" text="Du får eit referansenummer med ein gong." done={stepDone[3]} testid="public-bestill-step-send">
                  {/* Compact summary on small screens (the ticket is in the sidebar on desktop) */}
                  <dl className="mb-6 grid gap-x-6 gap-y-2 rounded-2xl bg-secondary/70 p-4 text-[14px] sm:grid-cols-2 lg:hidden" data-testid="public-bestill-mobile-summary">
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Gjeld</dt><dd className="text-right font-medium">{summary.service || summary.market}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Stad</dt><dd className="truncate text-right">{summary.address || "—"}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Tidspunkt</dt><dd className="text-right">{summary.timing || "—"}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Vedlegg</dt><dd className="text-right">{summary.files || "—"}</dd></div>
                  </dl>
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-sm text-[13.5px] leading-relaxed text-muted-foreground">Innsendinga er ei førespurnad, ikkje ei bindande bestilling. Vi avtaler prisgrunnlag og tidspunkt med deg før arbeidet startar.</p>
                    <button
                      type="submit"
                      disabled={busy}
                      className="link-arrow inline-flex h-[52px] w-full shrink-0 items-center justify-center gap-2 rounded-[12px] bg-brand px-7 text-base font-medium text-brand-foreground transition-[background-color,transform] duration-150 hover:bg-brand-dark active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60 sm:w-auto"
                      data-testid="public-bestill-submit-button"
                    >
                      {busy ? "Sender …" : <>Send førespurnad <ArrowRight className="h-4 w-4" /></>}
                    </button>
                  </div>
                </FormSection>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        {/* Sidebar */}
        <aside className="hidden min-w-0 lg:col-span-4 lg:block">
          <div className="lg:sticky lg:top-24">
            {ticket}
            <div className="mt-6 divide-y divide-border border-y border-border" data-testid="public-contact-alternatives">
              <a href={COMPANY.phoneHref} className="group flex items-center gap-4 py-4" data-testid="public-contact-alt-phone">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-foreground"><Phone className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1"><span className="block text-[15px] font-medium">Ring oss</span><span className="tabular block text-[13.5px] text-muted-foreground">{COMPANY.phone}</span></span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5" />
              </a>
              <a href={`mailto:${COMPANY.email}`} className="group flex items-center gap-4 py-4" data-testid="public-contact-alt-email">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-foreground"><Mail className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1"><span className="block text-[15px] font-medium">Send e-post</span><span className="block truncate text-[13.5px] text-muted-foreground">{COMPANY.email}</span></span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5" />
              </a>
              <button type="button" onClick={openChat} className="group flex w-full items-center gap-4 py-4 text-left" data-testid="public-contact-alt-chat">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-foreground"><MessageCircle className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1"><span className="block text-[15px] font-medium">Spør oss</span><span className="block text-[13.5px] text-muted-foreground">Assistenten svarer på tenester og prisar</span></span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>
        </aside>
      </section>

      {/* ---------- Other ways in (mobile gets the alternatives here) ---------- */}
      <section className="container-public mt-20 sm:mt-28" data-testid="public-contact-channels">
        <SectionHead eyebrow="Andre måtar å nå oss" title="Vil du heller ringje, sende e‑post eller spørje assistenten?" />
        <div className="mt-10 grid gap-x-8 gap-y-8 sm:grid-cols-3">
          {[
            { icon: Phone, label: "Telefon", value: COMPANY.phone, text: "Sentralbordet set deg over til rett avdeling.", href: COMPANY.phoneHref, testid: "public-contact-channel-phone" },
            { icon: Mail, label: "E-post", value: COMPANY.email, text: "For tilbod, dokument og spørsmål som ikkje hastar.", href: `mailto:${COMPANY.email}`, testid: "public-contact-channel-email" },
            { icon: MessageCircle, label: "Spør oss", value: "Opne assistenten", text: "Svar på vanlege spørsmål om tenester og prisar, døgnet rundt.", onClick: openChat, testid: "public-contact-channel-chat" },
          ].map(({ icon: Icon, label, value, text, href, onClick, testid }) => {
            const inner = (
              <>
                <span className="flex items-center justify-between"><Icon className="h-5 w-5 text-brand" strokeWidth={1.75} /><ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5" /></span>
                <span className="mt-8 block text-[13px] text-muted-foreground">{label}</span>
                <span className={cn("mt-1 block font-display text-[1.35rem] font-medium leading-tight tracking-[-0.02em] sm:text-[1.5rem]", label === "Telefon" && "tabular")}>{value}</span>
                <span className="mt-2 block max-w-[30ch] text-[14.5px] leading-relaxed text-muted-foreground">{text}</span>
              </>
            );
            const cls = "group block w-full border-t border-border pt-5 text-left transition-colors duration-150 hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 rounded-sm";
            return href ? <a key={label} href={href} className={cls} data-testid={testid}>{inner}</a> : <button key={label} type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
          })}
        </div>
      </section>

      {/* ---------- Offices ---------- */}
      <section className="grain relative mt-20 overflow-hidden bg-ink py-16 text-white sm:mt-28 sm:py-24" data-testid="public-contact-offices">
        <div className="container-public relative grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-6">
            <Eyebrow light>Kontor og avdelingar</Eyebrow>
            <h2 className="t-h2 mt-4 text-white">Rosendal, Husnes og Halsnøy</h2>
            <p className="mt-5 max-w-lg text-[16.5px] leading-relaxed text-white/65">Hovudkontoret ligg i Rosendal. Avdelingane på Husnes og Halsnøy tek service og installasjon i sine område, og større prosjekt styrer vi der arbeidet er.</p>
            <ul className="mt-10 divide-y divide-white/10 border-y border-white/10">
              {OFFICES.map((o) => (
                <li key={o.name} className="grid gap-2 py-5 sm:grid-cols-12 sm:gap-6" data-testid={`public-contact-office-${o.name.toLowerCase()}`}>
                  <div className="flex items-center gap-3 sm:col-span-5">
                    <span aria-hidden className="h-2 w-2 rounded-full bg-brand" />
                    <span className="font-display text-[1.25rem] font-medium tracking-[-0.02em]">{o.name}</span>
                  </div>
                  <div className="sm:col-span-7">
                    <p className="text-[13px] text-white/50">{o.role}</p>
                    <p className="mt-0.5 text-[15px] text-white/85">{o.lines.join(", ")}</p>
                  </div>
                </li>
              ))}
            </ul>
            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 text-[15px]">
              <div><dt className="text-white/50">Postadresse</dt><dd className="mt-1 text-white/90">{COMPANY.hq.post.join(", ")}</dd></div>
              <div><dt className="text-white/50">Føretak</dt><dd className="mt-1 text-white/90">{COMPANY.legal}</dd></div>
            </dl>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={0.1}>
            <Photo src={IMG.village} alt="Trehus og kai ved fjorden i Kvinnherad, med fjell i bakgrunnen." ratio="aspect-[4/3] lg:aspect-[5/4]" dark sizes="(min-width: 1024px) 50vw, 100vw" />
          </Reveal>
        </div>
      </section>

      {/* ---------- People ---------- */}
      <section className="container-public mt-20 sm:mt-28" data-testid="public-contact-people">
        <SectionHead eyebrow="Kontaktpersonar" title="Vil du snakke med nokon spesielt?" text="Gjeld det ei ny førespurnad, bruk gjerne skjemaet, så kjem ho til rett person hos oss." />
        <ul className="mt-10 grid gap-x-10 border-t border-border sm:grid-cols-2">
          {PEOPLE.map((p) => (
            <li key={p.email} className="grid gap-1 border-b border-border py-5 sm:grid-cols-12 sm:gap-6" data-testid="public-contact-person">
              <div className="min-w-0 sm:col-span-7">
                <p className="text-[16px] font-medium">{p.name}</p>
                <p className="mt-0.5 text-[13.5px] leading-snug text-muted-foreground">{p.title}</p>
              </div>
              <div className="min-w-0 sm:col-span-5 sm:text-right">
                <a href={`tel:+47${p.phone.replace(/\s/g, "")}`} className="u-link tabular block text-[15px] font-medium">{p.phone}</a>
                <a href={`mailto:${p.email}`} className="u-link block truncate text-[13.5px] text-muted-foreground">{p.email}</a>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="container-public mt-20 grid gap-10 sm:mt-28 lg:grid-cols-12 lg:gap-16" data-testid="public-contact-faq">
        <div className="lg:col-span-5">
          <Eyebrow>Før du sender</Eyebrow>
          <h2 className="t-h2 mt-4 max-w-md">Vanlege spørsmål</h2>
          <p className="mt-5 max-w-md text-[15.5px] leading-relaxed text-muted-foreground">Finn du ikkje svaret her, så er det berre å spørje i skjemaet eller ringje oss.</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Btn href={COMPANY.phoneHref} variant="outline" data-testid="public-contact-faq-phone"><Phone className="h-4 w-4" />Ring {COMPANY.phone}</Btn>
            <Btn variant="ghost" onClick={openChat} data-testid="public-contact-faq-chat"><MessageCircle className="h-4 w-4" />Spør oss</Btn>
          </div>
        </div>
        <div className="lg:col-span-7">
          <Accordion type="single" collapsible className="border-t border-border" data-testid="public-contact-faq-list">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={`q${i}`} className="border-border" data-testid={`public-contact-faq-item-${i}`}>
                <AccordionTrigger className="gap-6 py-5 text-left text-[16.5px] font-medium hover:no-underline [&>svg]:h-5 [&>svg]:w-5">
                  <span className="flex items-baseline gap-4"><span className="t-mono w-6 shrink-0">0{i + 1}</span>{q}</span>
                </AccordionTrigger>
                <AccordionContent className="pb-6 pl-10 text-[15.5px] leading-relaxed text-muted-foreground">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <div className="h-8 sm:h-12" aria-hidden />
    </div>
  );
}
